import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import styles from './top-menu.module.scss';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import ProfileButton from '../../../components/atoms/profile-button/profile-button';
import SearchBar from '../../../components/molecules/search-bar/search-bar';
import LanguageSwitcher from '../../../components/atoms/language-switcher/language-switcher';
import MobileNavigation from '../mobile-navigation/mobile-navigation';
import IMenuProfile from '../../../models/header/IMenuProfile';
import IMenuItem from '../../../models/header/IMenuItem';
import { ApplicationSettings } from '../../../models/settings';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { HamburgerIcon, SearchIcon } from '../../atoms/icons/icons';
import Button from '../../atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ButtonSizeType } from '../../../constants/button-size-type';
import useOnClickOutside from '../../../hooks/use-on-click-outside.hook';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Logo } from './logo';
import Navigation from '../../molecules/desktop-navigation/navigation';
import { useTranslations } from 'next-intl';

type Props = {
  profile?: IMenuProfile;
  menuItems: IMenuItem[];
  locales: { text: string; value: string }[];
  isShowSearchBar?: boolean;
  applicationSettings: ApplicationSettings;
  currentLocale: string;
  pathname: string;
  brandLogo?: string;
  brandLogoAltText?: string;
  brandLogoURL?: string;
  themeColor?: string;
  themeCopyColor?: string;
  invertedTheme?: boolean;
  brandPrimaryColor?: string;
  headerApplyURL?: string;
  headerApplyCopyText?: string;
  headerSearchPlaceholder?: string;
  isLargerLogo: boolean;
};

export default function TopMenu({
  profile,
  menuItems,
  locales,
  isShowSearchBar,
  applicationSettings,
  currentLocale,
  pathname,
  brandLogo,
  brandLogoAltText,
  themeColor,
  themeCopyColor,
  invertedTheme,
  brandPrimaryColor,
  headerApplyURL,
  headerApplyCopyText,
  headerSearchPlaceholder,
  isLargerLogo,
}: Props) {
  const [userProfile, setUserProfile] = useState(profile?.userProfile);
  const [isProfileLoading, setIsProfileLoading] = useState(profile?.loadingProfile);
  const [openMobileSearch, setOpenMobileSearch] = useState<boolean>(false);
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false);

  useEffect(() => {
    setUserProfile(profile?.userProfile);
    setIsProfileLoading(profile?.loadingProfile);
  }, [profile]);

  const topBarStyle: CSSProperties = {
    background: themeColor || colorStyle.grey1,
    color: themeCopyColor || colorStyle.white,
  };

  const renderStyles = useMemo(() => {
    return {
      mobileTrigger: {
        '--menu-color': themeColor,
        '--menu-copy-color': themeCopyColor,
        '--brand-primary-color': brandPrimaryColor,
      } as CSSProperties,
    };
  }, [themeColor, themeCopyColor, brandPrimaryColor]);

  const handleSideMenuTriggerClick = () => {
    setOpenSideMenu(true);
    setOpenMobileSearch(false);
    // Reset the flag shortly after to avoid repeated triggers
    setTimeout(() => {
      setOpenSideMenu(false);
    }, 0);
  };

  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpenMobileSearch(false));

  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);
  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= parseInt(breakpoints.lg, 10)), 100);

  const translate = useTranslations(`Common`);

  return (
    <>
      <div
        className={`${styles[`header-top`]} d-flex align-items-center`}
        style={topBarStyle}
        id='TitleBar'
        ref={ref}
      >
        <div className='container'>
          <div className={`${styles[`header-top_content`]} d-flex justify-content-between`}>
            <Logo
              invertedTheme={invertedTheme}
              currentLocale={currentLocale}
              brandLogo={brandLogo}
              brandLogoAltText={brandLogoAltText}
              isBreakpointUp={isBreakpointUp}
              isLarger={isLargerLogo}
            />

            <div className='align-items-center col d-flex relative'>
              {isShowSearchBar && (
                <div>
                  <div
                    className={`${styles['header-search']} ${styles['header-search-mobile']} ${openMobileSearch ? styles['header-search-mobile_open'] : `${styles['header-search-mobile_close']}`}`}
                  >
                    <SearchBar
                      idPrefix='typeahead-search'
                      cssWrapper='header'
                      typeAheadPlaceHolder={
                        headerSearchPlaceholder
                          ? headerSearchPlaceholder
                          : translate('header.search-placeholder')
                      }
                      applicationSettings={applicationSettings}
                      currentLocale={currentLocale}
                      themeColor={themeColor}
                      themeCopyColor={themeCopyColor}
                      invertedTheme={invertedTheme}
                      brandPrimaryColor={brandPrimaryColor}
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              className={`${styles[`header-top_actions`]} d-flex align-items-center gap-md-4 gap-3`}
            >
              {headerApplyCopyText && headerApplyURL && (
                <span className='d-lg-inline-block d-none'>
                  <Button
                    buttonID='header-apply-cta'
                    buttonAriaLabel='cta to help students apply to offered courses'
                    buttonName='apply cta'
                    buttonStyle={ButtonStyleType.Outline}
                    buttonSize={ButtonSizeType.Medium}
                    buttonURL={headerApplyURL}
                    copyColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                    outlineColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                    backgroundColor={colorStyle.transparent}
                    hoverBackgroundColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                    activeBackgroundColor={colorStyle.transparent}
                    buttonRole='button'
                    customClass='header-apply-cta'
                  >
                    {headerApplyCopyText}
                  </Button>
                </span>
              )}
              <span className='d-lg-inline-block d-none'>
                <LanguageSwitcher
                  locales={locales}
                  activeLocale={currentLocale}
                  themeCopyColor={themeCopyColor}
                  brandPrimaryColor={brandPrimaryColor}
                  application={applicationSettings.applicationType}
                  invertedTheme={invertedTheme}
                />
              </span>
              {isShowSearchBar && (
                <button
                  type='button'
                  className={`${styles['mobile-trigger']} ${invertedTheme ? styles['inverted-theme'] : ''} d-lg-none image-wrapper`}
                  onClick={() => setOpenMobileSearch(!openMobileSearch)}
                  title='Toggle Mobile Search'
                  style={renderStyles.mobileTrigger}
                  aria-expanded={openMobileSearch}
                  aria-label={`${openMobileSearch ? 'Close' : 'Open'} search`}
                >
                  <SearchIcon fillColor={colorStyle.white} height={24} width={24} />
                </button>
              )}
              {profile && (
                <div className='d-flex'>
                  {isProfileLoading ? (
                    <LoadingSpinner
                      spinnerId='loading-spinner-login'
                      src='/public/next-assets/loaders/into.svg'
                      width={30}
                      height={30}
                    />
                  ) : (
                    <ProfileButton
                      url={userProfile ? profile.signOutUrl : profile.signInUrl}
                      state={userProfile ? 'signOut' : 'signIn'}
                    />
                  )}
                </div>
              )}
              {/* Hamburger Button */}
              <button
                type='button'
                className={`${styles['mobile-trigger']} ${invertedTheme ? styles['inverted-theme'] : ''} d-lg-none image-wrapper`}
                onClick={handleSideMenuTriggerClick}
                title='Toggle Mobile Navigation'
                style={renderStyles.mobileTrigger}
              >
                <HamburgerIcon fillColor={colorStyle.white} height={24} width={24} />
              </button>
            </div>

            <MobileNavigation
              items={menuItems}
              currentLocale={currentLocale}
              brandLogo={brandLogo}
              brandLogoAltText={brandLogoAltText}
              themeColor={themeColor}
              themeCopyColor={themeCopyColor}
              invertedTheme={invertedTheme}
              brandPrimaryColor={brandPrimaryColor}
              headerApplyURL={headerApplyURL}
              headerApplyCopyText={headerApplyCopyText}
              openSideMenu={openSideMenu}
              locales={locales}
              applicationType={applicationSettings.applicationType}
            />
          </div>
        </div>
      </div>

      <Navigation
        menuItems={menuItems}
        pathname={pathname}
        themeColor={themeColor}
        themeCopyColor={themeCopyColor}
        invertedTheme={invertedTheme}
        brandPrimaryColor={brandPrimaryColor}
      />
    </>
  );
}
